import { Divider, Grid, ListItem } from "@mui/material";
import { isEmpty, useDataProvider, useNotify, useRecordContext } from "ra-core";
import { Fragment, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { padding2, textColor, width } from "../Styles";
import { maxLengthValidation, requiredValidation } from "../../utils/util";
import {
  AutocompleteInput,
  BooleanInput,
  Loading,
  SelectInput,
  TextInput,
} from "ra-ui-materialui";
import {
  RESOURCES,
  THEMABLE_REPORT_TYPE_CHOICES,
} from "../Constants";
import JsonSchemaInput from "./JsonSchemaInput";
import { Error404 } from "../errors/Error404";

export const ReportThemeFormGrid = (props) => {
  const { isEdit, configurationPresent, setConfigurationPresent } = props;

  const values = useWatch();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const record = useRecordContext();
  const form = useFormContext();

  const [jsonSchema, setJsonSchema] = useState(null);
  const [uiSchema, setUiSchema] = useState(null);
  const [schemaPresent, setSchemaPresent] = useState(false);
  const [clientChoices, setClientChoices] = useState();
  const [reportThemeChoices, setReportThemeChoices] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  const filteredReportThemeChoices = isEmpty(values?.reportType)
    ? reportThemeChoices
    : reportThemeChoices.filter(
        (reportTheme) => reportTheme.reportType === values.reportType,
      );

  const filteredReportTemplateChoices = reportTemplates.filter(
    (template) => template.reportThemeId === values?.reportThemeId,
  );
  const hasReportTemplates = !isEmpty(filteredReportTemplateChoices);
  const showDynamicForm =
    !hasReportTemplates || values?.customisation === true;

  useEffect(() => {
    setSchemaPresent(false);
    if(!isEdit){
      setConfigurationPresent(false)
    }    
    if (!isEmpty(values?.clientId) && !isEmpty(values?.reportThemeId)) {
      dataProvider
        .getReportThemeSchema(values.reportThemeId)
        .then(({ data }) => {
          form.setValue("reportThemeVersionId", data.reportThemeVersionId);
          setJsonSchema(JSON.parse(data.configurationSchema));
          setUiSchema(JSON.parse(data.configurationUiSchema));
          setSchemaPresent(true);
        })
        .catch(() => {
          notify("Error while fetching report theme schema", { type: "error" });
        });

      if (!isEdit) {
        if(showDynamicForm){
          dataProvider
          .getList(RESOURCES.REPORT_THEME, {
            filter: {
              clientId: values.clientId,
              reportThemeId: values.reportThemeId              
            },
            pagination: { page: 1, perPage: 20 },
            sort: { field: "createdAt", order: "DESC" },
          })
          .then(({ data }) => {
            const dynamicConfigurations = data.filter((entry) => isEmpty(entry.reportThemeTemplateId));            
            setConfigurationPresent(dynamicConfigurations.length > 0);
          });
        }else if(!isEmpty(values.reportThemeTemplateId)){
          dataProvider
          .getOne(RESOURCES.REPORT_THEME, { id: `client/${values.clientId}/template/${values.reportThemeTemplateId}` })
          .then(({ data }) => {
            setConfigurationPresent(true);
          })
          .catch((err) => {
            setConfigurationPresent(false);
          });
        }         
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.clientId, values.reportThemeId, isEdit, values.reportThemeTemplateId, values.customisation, showDynamicForm]);

  useEffect(() => {
    if (!isEdit) {
      form.setValue("reportThemeId", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.reportType]);

  useEffect(() => {
    if (!isEdit) {
      form.setValue("reportThemeTemplateId", undefined);
      form.setValue("customisation", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.reportThemeId]);

  useEffect(() => {
    Promise.all([
      dataProvider.getClientNameList("admin/client/names"),
      dataProvider.getAllReportThemes(),
      dataProvider.getAllReportThemeTemplates(),
    ])
      .then(([clientData, reportTheme, templates]) => {
        setClientChoices(clientData.data);
        setReportThemeChoices(reportTheme.data);
        setReportTemplates(templates.data);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;
  if (error) return <Error404 />;

  return (
    <Grid container flexGrow={1}>
      <Grid item xs={12}>
        <ListItemComponentForReportTheme
          title="Client Name"
          required={true}
          input={
            <AutocompleteInput
              id="report-theme-form-client-input"
              sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
              source="clientId"
              name="clientId"
              variant="outlined"
              size="small"
              label="Select a client"
              choices={clientChoices}
              validate={requiredValidation}
              readOnly={isEdit}
            />
          }
        />
        <Divider light />
        <ListItemComponentForReportTheme
          title="Report Type"
          required={false}
          input={
            <SelectInput
              id="report-theme-form-report-type-input"
              sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
              source="reportType"
              name="reportType"
              variant="outlined"
              size="small"
              label="Select a report type"
              choices={THEMABLE_REPORT_TYPE_CHOICES}
              validate={requiredValidation}
              readOnly={isEdit}
            />
          }
        />
        {!isEmpty(values?.reportType) && (
          <>
            <Divider light />
            <ListItemComponentForReportTheme
              title="Report Theme"
              required={false}
              input={
                <SelectInput
                  id="report-theme-form-report-theme-input"
                  sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
                  source="reportThemeId"
                  name="reportThemeId"
                  variant="outlined"
                  size="small"
                  label="Select a Report Theme"
                  choices={filteredReportThemeChoices}
                  validate={requiredValidation}
                  readOnly={isEdit}
                />
              }
            />
          </>
        )}
        {hasReportTemplates && (
          <>
            <Divider light />
            <ListItemComponentForReportTheme
              title="Customisation"
              required={false}
              input={
                <BooleanInput
                  id="report-theme-form-customisation-input"
                  sx={{ marginBottom: "-15px" }}
                  source="customisation"
                  name="customisation"
                  label="Build a custom theme"
                  defaultValue={true}
                  readOnly={isEdit}
                />
              }
            />
          </>
        )}
        {hasReportTemplates && !showDynamicForm && (
          <>
            <Divider light />
            <ListItemComponentForReportTheme
              title="Report Template"
              required={false}
              input={
                <SelectInput
                  id="report-theme-form-report-template-input"
                  sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
                  source="reportThemeTemplateId"
                  name="reportThemeTemplateId"
                  variant="outlined"
                  size="small"
                  label="Select a Report Template"
                  choices={filteredReportTemplateChoices}
                  validate={requiredValidation}
                  readOnly={isEdit}
                />
              }
            />
          </>
        )}
        <Divider light />
        {!isEdit && schemaPresent && configurationPresent && (
          <div
            style={{
              color: "orangered",
              justifySelf: "center",
              fontSize: "large",
            }}
            data-testid="theme-exist-error-message"
          >
            Client's configurations exist for this theme
          </div>
        )}
        {schemaPresent && showDynamicForm && (
          <JsonSchemaInput
            source="configurations"
            schema={jsonSchema}
            uiSchema={uiSchema}
            defaultValue={isEdit ? record?.configuration : undefined}
            isEdit={isEdit}
          />
        )}
        {!isEdit && schemaPresent && showDynamicForm && (
          <>
            <ListItemComponentForReportTheme
              title="Version Comment"
              required={false}
              input={
                <TextInput
                  key="versionComment"
                  source="versionComment"
                  name="versionComment"
                  label="Version Comment"
                  variant="outlined"
                  size="small"
                  sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
                  multiline={true}
                  validate={[requiredValidation, maxLengthValidation(250)]}
                />
              }
            />
            <Divider light />
          </>
        )}
        {isEdit && schemaPresent && showDynamicForm && (
          <>
            <ListItemComponentForReportTheme
              title="Version Comment"
              required={false}
              input={
                <TextInput
                  key="addVersionComment"
                  source="addVersionComment"
                  name="addVersionComment"
                  label="Version Comment"
                  variant="outlined"
                  size="small"
                  sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
                  multiline={true}
                  validate={[requiredValidation, maxLengthValidation(250)]}
                />
              }
            />
            <Divider light />
          </>
        )}
      </Grid>
    </Grid>
  );
};

export const ListItemComponentForReportTheme = (props) => {
  return (
    <Fragment>
      <Grid>
        <ListItem sx={padding2.y}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={4} sx={textColor.gray}>
              {props.title}
              {props.required && (
                <span style={{ ...textColor.red, marginLeft: "3px" }}>*</span>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              {props.input}
            </Grid>
          </Grid>
        </ListItem>
      </Grid>
    </Fragment>
  );
};
